import React from "react";
import PropTypes from "prop-types";
import { Empty, Spin, Modal, message } from "antd";
import { AddressRecord } from "records/company-record";
import AddAddressForm from "forms/add-address-form";
import { CompanyService } from "services/index";
import SectionBlock from "./components/section-block";
import CardBlock from "./components/card-block";
import KycSummaryTable from "./components/kyc-summary-table";
import KycViewerModal from "./components/kyc-viewer-modal";
// Bureau Remarks section commented out per request.
// import BureauRemarks from "./components/bureau-remarks";
import { SECTION_IDS } from "./constants";

// ADDRESSES_TYPES: 5 = Residence, 6 = Permanent. Both are editable inline.
const ADDRESS_TYPE_RESIDENCE = 5;
const ADDRESS_TYPE_PERMANENT = 6;

const ADDRESS_TYPE_LABEL = {
    [ADDRESS_TYPE_RESIDENCE]: "Residence",
    [ADDRESS_TYPE_PERMANENT]: "Permanent"
};

// AddressRecord's own keys. The API address object carries extras (city_name,
// created_date, …) that are not part of the Record definition, so pick only
// what the Record + its `formValues` getter understand.
const ADDRESS_KEYS = [
    "id",
    "company",
    "associate",
    "address_type",
    "ownership_type",
    "residing_since",
    "address_line_1",
    "address_line_2",
    "city",
    "district",
    "state",
    "country",
    "pincode",
    "is_pv_done",
    "is_communication_address",
    "is_active"
];

function pickAddressFields(raw) {
    const out = {};
    if (!raw) {
        return out;
    }
    ADDRESS_KEYS.forEach(key => {
        if (raw[key] !== undefined) {
            out[key] = raw[key];
        }
    });
    return out;
}

/**
 * Applicant Details section.
 *  - KYC matrix: at-a-glance identity, documents, CIBIL and bureau remarks
 *    across all applicants (main borrower first).
 *  - Document viewer modal: opened from the matrix; the CIBIL tab shows the
 *    bureau report with the bureau remarks highlighted.
 */
class ApplicantDetailsSection extends React.Component {
    state = {
        viewerOpen: false,
        viewerIndex: 0,
        viewerTab: "pan",
        // Address editor state: the applicant being edited (null = closed) plus
        // the type (Residence 5 / Permanent 6). Same modal + form handle both.
        addrApplicant: null,
        addrType: null,
        addrSaving: false
    };

    addrFormRef = React.createRef();

    // Credit Appraisal team (Credit Manager group 7, Credit Administrator
    // group 11) is not allowed to open applicant KYC / bureau docs — the
    // ask is to strictly separate credit appraisal from document review.
    // Click passes through this guard: shows a warning toast instead of
    // opening the viewer.
    isDocViewBlocked = () => {
        const g = this.props.userGroups;
        if (!Array.isArray(g)) return false;
        return g.includes(7) || g.includes(11);
    };

    openViewer = (index, tab) => {
        if (this.isDocViewBlocked()) {
            message.warning(
                "You don't have permission to view applicant documents.",
                3
            );
            return;
        }
        this.setState({
            viewerOpen: true,
            viewerIndex: index,
            viewerTab: tab || "photo"
        });
    };

    closeViewer = () => this.setState({ viewerOpen: false });

    setIndex = index => this.setState({ viewerIndex: index });

    // ── Address editor (Residence or Permanent) ──────────────────────────────
    openAddress = (index, type) => {
        const applicant = (this.props.applicants || [])[index];
        if (applicant) {
            this.setState({ addrApplicant: applicant, addrType: type });
        }
    };

    openResidenceAddress = index => this.openAddress(index, ADDRESS_TYPE_RESIDENCE);
    openPermanentAddress = index => this.openAddress(index, ADDRESS_TYPE_PERMANENT);

    closeAddressModal = () =>
        this.setState({
            addrApplicant: null,
            addrType: null,
            addrSaving: false
        });

    saveAddress = () => {
        const { addrApplicant, addrType } = this.state;
        const { companyId, caseId } = this.props;
        const form = this.addrFormRef.current;
        if (!addrApplicant || !addrType || !companyId || !form) {
            return;
        }
        const isPermanent = addrType === ADDRESS_TYPE_PERMANENT;
        const existing = isPermanent
            ? addrApplicant.permanentAddressRecord
            : addrApplicant.residenceAddressRecord;
        const label = ADDRESS_TYPE_LABEL[addrType] || "Address";
        form.validateFields((err, values) => {
            if (err) {
                return;
            }
            this.setState({ addrSaving: true });
            CompanyService.updateSingleAddress(
                companyId,
                new AddressRecord({
                    ...pickAddressFields(values),
                    // Carry the existing row's id so the endpoint updates it
                    // rather than creating a duplicate (null => create).
                    id: (existing && existing.id) || null,
                    company: companyId,
                    associate: addrApplicant.associateId,
                    address_type: addrType
                }).formValues,
                caseId
            ).then(
                () => {
                    // Re-read the company profile (addresses live on it) before
                    // anything else, so the table reflects the server.
                    if (this.props.onAddressSaved) {
                        this.props.onAddressSaved();
                    }
                    this.closeAddressModal();
                    message.success(`${label} address updated`);
                },
                // Second arg (not .catch) so a post-save error is never
                // reported as a failed save.
                () => {
                    this.setState({ addrSaving: false });
                    message.error(`Failed to update ${label.toLowerCase()} address`);
                }
            );
        });
    };

    renderAddressModal() {
        const { addrApplicant, addrType } = this.state;
        const { companyId } = this.props;
        const isPermanent = addrType === ADDRESS_TYPE_PERMANENT;
        const label = addrType ? ADDRESS_TYPE_LABEL[addrType] : "";
        const existing =
            addrApplicant &&
            (isPermanent
                ? addrApplicant.permanentAddressRecord
                : addrApplicant.residenceAddressRecord);
        const hasExisting =
            addrApplicant &&
            (isPermanent
                ? addrApplicant.permanentAddress
                : addrApplicant.residenceAddress);
        return (
            <Modal
                visible={Boolean(addrApplicant && addrType)}
                destroyOnClose
                title={
                    addrApplicant
                        ? `${hasExisting ? "Edit" : "Add"} ${label} Address — ${addrApplicant.name}`
                        : ""
                }
                okText={"Save"}
                confirmLoading={this.state.addrSaving}
                onOk={this.saveAddress}
                onCancel={this.closeAddressModal}
            >
                {addrApplicant && addrType ? (
                    <AddAddressForm
                        ref={this.addrFormRef}
                        values={
                            new AddressRecord({
                                ...pickAddressFields(existing),
                                company: companyId,
                                associate: addrApplicant.associateId,
                                address_type: addrType
                            })
                        }
                        addressTypes={[addrType]}
                    />
                ) : null}
            </Modal>
        );
    }

    // Applicants KYC edits (relationship, shareholding, addresses, main
    // borrower toggle) are only actionable at the credit stages — same gate
    // used elsewhere in Credit Checker (TVR reset, bank statement rebuild).
    canEditKyc = () => [7, 8].includes(Number(this.props.stageId));

    render() {
        const {
            innerRef,
            caseId,
            applicants,
            loading,
            expanded,
            onToggle,
            onSaveBureauRemark,
            savingRemarkId,
            onToggleMainBorrower,
            savingMainBorrowerId
        } = this.props;
        const hasApplicants = applicants && applicants.length;
        const canEdit = this.canEditKyc();

        return (
            <SectionBlock
                id={SECTION_IDS.APPLICANT_DETAILS}
                innerRef={innerRef}
                title={"Applicant Details"}
                subtitle={"KYC documents, identity and credit bureau per applicant"}
                icon={"idcard"}
                expanded={expanded}
                onToggle={onToggle}
            >
                <Spin spinning={Boolean(loading)}>
                    {hasApplicants ? (
                        <React.Fragment>
                            <CardBlock
                                icon={"idcard"}
                                title={`Applicants KYC (${applicants.length})`}
                                subtitle={"Documents, contact and bureau remarks"}
                            >
                                <KycSummaryTable
                                    applicants={applicants}
                                    caseId={caseId}
                                    applicantHits={this.props.applicantHits}
                                    docViewBlocked={this.isDocViewBlocked()}
                                    onView={(index, tab) =>
                                        this.openViewer(index, tab)
                                    }
                                    onToggleMainBorrower={
                                        canEdit ? onToggleMainBorrower : null
                                    }
                                    savingMainBorrowerId={savingMainBorrowerId}
                                    onEditPermanentAddress={
                                        this.props.companyId && canEdit
                                            ? this.openPermanentAddress
                                            : null
                                    }
                                    onEditResidenceAddress={
                                        this.props.companyId && canEdit
                                            ? this.openResidenceAddress
                                            : null
                                    }
                                    onChangeRelationship={
                                        canEdit
                                            ? this.props.onChangeRelationship
                                            : null
                                    }
                                    savingRelationshipId={
                                        this.props.savingRelationshipId
                                    }
                                    onUpdateShareholding={
                                        canEdit
                                            ? this.props.onUpdateShareholding
                                            : null
                                    }
                                    canEditShareholding={
                                        canEdit &&
                                        this.props.canEditShareholding
                                    }
                                    savingShareholdingId={
                                        this.props.savingShareholdingId
                                    }
                                    onUpdateEmail={
                                        canEdit
                                            ? this.props.onUpdateEmail
                                            : null
                                    }
                                    savingEmailId={this.props.savingEmailId}
                                />
                            </CardBlock>

                            {/* Bureau Remarks section commented out per request.
                            {onSaveBureauRemark ? (
                                <CardBlock
                                    icon={"edit"}
                                    title={"Bureau Remarks"}
                                    subtitle={
                                        "Analyst bureau remark per applicant — saved to the case"
                                    }
                                >
                                    <BureauRemarks
                                        applicants={applicants}
                                        savingId={savingRemarkId}
                                        onSave={onSaveBureauRemark}
                                    />
                                </CardBlock>
                            ) : null}
                            */}

                            <KycViewerModal
                                visible={this.state.viewerOpen}
                                applicants={applicants}
                                applicantHits={this.props.applicantHits}
                                index={this.state.viewerIndex}
                                initialTab={this.state.viewerTab}
                                onIndexChange={this.setIndex}
                                onClose={this.closeViewer}
                            />
                            {this.renderAddressModal()}
                        </React.Fragment>
                    ) : (
                        !loading && (
                            <Empty description={"No applicants on this case"} />
                        )
                    )}
                </Spin>
            </SectionBlock>
        );
    }
}

ApplicantDetailsSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Gates KYC edits (relationship, shareholding, addresses, main borrower
    // toggle) to the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onAddressSaved: PropTypes.func,
    onChangeRelationship: PropTypes.func,
    savingRelationshipId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onUpdateShareholding: PropTypes.func,
    canEditShareholding: PropTypes.bool,
    savingShareholdingId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    onUpdateEmail: PropTypes.func,
    savingEmailId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    // Map of applicant-name → { probable, confirmed, reportUrl } parsed from
    // the trackwizz payload by Credit Checker's index.js. Feeds the KYC row's
    // Probable / Confirmed hit columns and the Screening tab in the viewer.
    applicantHits: PropTypes.object,
    applicants: PropTypes.array,
    loading: PropTypes.bool,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    onSaveBureauRemark: PropTypes.func,
    savingRemarkId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onToggleMainBorrower: PropTypes.func,
    savingMainBorrowerId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
};

export default ApplicantDetailsSection;
